import { useRef, useState } from 'react';
import { Control, FieldValues, Path, PathValue, useController } from 'react-hook-form';
import { Button } from './button';
import { Label } from './label';
import { toast } from '@/hooks/use-toast';
import { uploadFileToS3ViaPreSignedUrl } from '@/utils';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { Loader2, Upload, X } from 'lucide-react';

type FileUploadProps<T extends FieldValues = FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  keyPrefix: string;
  accept?: string;
  defaultValue?: string;
  description?: string;
};

export function FileUpload<T extends FieldValues = FieldValues>({
  control,
  name,
  label,
  keyPrefix,
  accept,
  defaultValue,
  description,
}: FileUploadProps<T>) {
  const { field } = useController({
    control,
    name: name as Path<T>,
    defaultValue: (defaultValue as PathValue<T, Path<T>>) || undefined,
  });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getAccessToken } = useCognitoAuth();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadFileToS3ViaPreSignedUrl({
        file,
        keyPrefix,
        getAccessToken,
      });
      field.onChange(url);
      toast({
        title: 'Success',
        description: 'File uploaded successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload file',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    field.onChange(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const currentFileUrl = field.value as string | undefined;
  const fileName = currentFileUrl ? currentFileUrl.split('/').pop() : undefined;
  const isImage = accept?.includes('image') || false;

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={name}>{label}</Label>}
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          id={name}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          disabled={isUploading}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="flex items-center gap-2"
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              {currentFileUrl ? 'Replace File' : 'Upload File'}
            </>
          )}
        </Button>
        {currentFileUrl && !isImage && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <a
              href={currentFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-foreground hover:underline truncate"
            >
              {fileName}
            </a>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={isUploading}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      {currentFileUrl && isImage && (
        <div className="relative group">
          <div className="rounded-md border overflow-hidden bg-muted max-w-md">
            <img
              src={currentFileUrl}
              alt={label || 'Preview'}
              className="w-full h-auto object-contain max-h-64"
            />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <a
              href={currentFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-foreground hover:underline truncate flex-1 min-w-0"
            >
              {fileName}
            </a>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={isUploading}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
